<!--
  @Description 问题汇总 - 数据统计面板
  @Date 2026-07-20
  基于当前界面 tableData 数据，渲染4个维度的统计图表：
  1. 完成进度（堆叠水平柱状图，按人分组）
  2. 前后端工作量（分组柱状图，按人统计）
  3. 错误/完善分布（环形饼图）
  4. 问题收尾程度（仪表盘 + 节点分布饼图）
-->
<template>
  <div class="statistics-panel">
    <!-- <div class="statistics-header">
      <span class="statistics-title">
        <i class="el-icon-data-analysis"></i>
        数据统计
      </span>
      <span class="statistics-summary">
        共 <b>{{ tableData.length }}</b> 条数据
      </span>
    </div> -->
    <div class="statistics-charts">
      <!-- 完成进度 -->
      <div class="chart-card clickable">
        <div class="chart-card-title">
          完成进度
          <!-- <span class="chart-card-tip">（点击可查看明细）</span> -->
        </div>
        <div ref="progressChart" class="chart-container"></div>
      </div>
      <!-- 工作量统计 -->
      <div class="chart-card">
        <div class="chart-card-title">工作量统计</div>
        <div ref="workloadChart" class="chart-container"></div>
      </div>
      <!-- 错误/完善分布 -->
      <div class="chart-card">
        <div class="chart-card-title">类型分布</div>
        <div ref="typeChart" class="chart-container"></div>
      </div>
      <!-- 问题收尾程度 -->
      <div class="chart-card">
        <div class="chart-card-title">收尾程度</div>
        <div ref="closureChart" class="chart-container"></div>
      </div>
    </div>
  </div>
</template>

<script>
import * as echarts from 'echarts'
import XEUtils from 'xe-utils'

export default {
  name: 'StatisticsPanel',
  props: {
    tableData: {
      type: Array,
      default: () => []
    },
    // 传入 Summary 实例，用于打开只读预览弹窗
    parentObj: {
      type: Object,
      default: null
    }
  },
  data() {
    return {
      // ECharts 实例
      charts: {
        progress: null,
        workload: null,
        type: null,
        closure: null
      }
    }
  },
  watch: {
    tableData: {
      handler() {
        this.$nextTick(() => {
          this.renderAllCharts()
        })
      },
      deep: true
    }
  },
  mounted() {
    this.$nextTick(() => {
      this.initCharts()
      this.renderAllCharts()
    })
    window.addEventListener('resize', this.handleResize)
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.handleResize)
    this.disposeCharts()
  },
  methods: {
    // 初始化图表实例
    initCharts() {
      this.charts.progress = echarts.init(this.$refs.progressChart)
      this.charts.workload = echarts.init(this.$refs.workloadChart)
      this.charts.type = echarts.init(this.$refs.typeChart)
      this.charts.closure = echarts.init(this.$refs.closureChart)
    },
    // 销毁图表实例
    disposeCharts() {
      Object.values(this.charts).forEach(chart => {
        if (chart) {
          chart.dispose()
        }
      })
    },
    // 窗口大小变化时自适应
    handleResize() {
      Object.values(this.charts).forEach(chart => {
        if (chart) {
          chart.resize()
        }
      })
    },
    // 渲染所有图表
    renderAllCharts() {
      if (!this.tableData) return
      this.renderProgressChart()
      this.renderWorkloadChart()
      this.renderTypeChart()
      this.renderClosureChart()
    },

    // ======================== 图表1：完成进度 ========================
    renderProgressChart() {
      const stats = this.computeProgressStats()
      const option = {
        tooltip: {
          trigger: 'axis',
          axisPointer: { type: 'shadow' },
          formatter: params => {
            let tip = `<b>${params[0].name}</b><br/>`
            params.forEach(p => {
              tip += `${p.marker} ${p.seriesName}：${p.value}<br/>`
            })
            return tip
          }
        },
        legend: {
          data: ['已完成', '未完成'],
          top: 5,
          textStyle: { fontSize: 11, color: '#666' }
        },
        grid: {
          left: 10,
          right: 30,
          top: 30,
          bottom: 5,
          containLabel: true
        },
        xAxis: {
          type: 'value',
          axisLabel: { fontSize: 10, color: '#999' },
          splitLine: { lineStyle: { type: 'dashed', color: '#eee' } }
        },
        yAxis: {
          type: 'category',
          data: stats.names,
          axisLabel: { fontSize: 10, color: '#333' },
          axisLine: { show: false },
          axisTick: { show: false }
        },
        series: [
          {
            name: '已完成',
            type: 'bar',
            stack: 'total',
            data: stats.completed,
            label: {
              show: true,
              position: 'inside',
              formatter: params => (params.value !== '-' && params.value > 0 ? params.value : ''),
              color: '#fff',
              fontSize: 10
            },
            itemStyle: {
              color: new echarts.graphic.LinearGradient(0, 0, 1, 0, [
                { offset: 0, color: '#67c23a' },
                { offset: 1, color: '#85ce61' }
              ]),
              borderRadius: [0, 0, 0, 0]
            },
            barMaxWidth: 20,
            barMinHeight: 20
          },
          {
            name: '未完成',
            type: 'bar',
            stack: 'total',
            data: stats.uncompleted,
            label: {
              show: true,
              position: 'inside',
              formatter: params => (params.value !== '-' && params.value > 0 ? params.value : ''),
              color: '#fff',
              fontSize: 10
            },
            itemStyle: {
              color: new echarts.graphic.LinearGradient(0, 0, 1, 0, [
                { offset: 0, color: '#f56c6c' },
                { offset: 1, color: '#f89898' }
              ]),
              borderRadius: [0, 0, 0, 0]
            },
            barMaxWidth: 20,
            barMinHeight: 20
          }
        ]
      }
      this.charts.progress.setOption(option, true)

      // 绑定柱子点击事件
      this.charts.progress.off('click')
      this.charts.progress.on('click', params => {
        this.handleProgressBarClick(params)
      })
    },

    // 计算完成进度统计数据
    computeProgressStats() {
      // 按角色分组：前端、后端、技术测试、业务测试
      const roleConfig = [
        { roleField: 'frontEngineer', dateField: 'frontResolvedDate', label: '前端' },
        { roleField: 'backEngineer', dateField: 'backResolvedDate', label: '后端' },
        { roleField: 'techTester', dateField: 'techTestDate', label: '技术测试' },
        { roleField: 'businessTester', dateField: 'businessTestDate', label: '业务测试' }
      ]

      // personMap: { '张三-前端': { completed: 0, uncompleted: 0 } }
      const personMap = {}

      this.tableData.forEach(row => {
        // 先计算研发是否已经完成（前端、后端）
        let frontDone = true
        let backDone = true
        if (row.frontEngineer && row.frontEngineer !== '/') {
          const fDate = String(row.frontResolvedDate || '')
          frontDone = Boolean(fDate && !fDate.includes('1900'))
        }
        if (row.backEngineer && row.backEngineer !== '/') {
          const bDate = String(row.backResolvedDate || '')
          backDone = Boolean(bDate && !bDate.includes('1900'))
        }
        const isDevDone = frontDone && backDone

        const resultStr = String(row.result || '')
        const caseStatusStr = String(row.caseStatus || '')
        const hasTechDate = String(row.techTestDate || '')
        const hasBusinessDate = String(row.businessTestDate || '')
        const hasAnyTestDate =
          (hasTechDate && !hasTechDate.includes('1900')) || (hasBusinessDate && !hasBusinessDate.includes('1900'))

        // 综合判断是否到达测试阶段
        let isTestingPhase = false
        if (/设计|研发/.test(resultStr)) {
          isTestingPhase = false
        } else {
          isTestingPhase =
            isDevDone || hasAnyTestDate || /测试|通过|已解决/.test(resultStr) || /测试|通过|已解决/.test(caseStatusStr)
        }

        // 统计“未设计”：前端和后端都没有人，且状态不是“通过”
        const noFront = !row.frontEngineer || row.frontEngineer === '/'
        const noBack = !row.backEngineer || row.backEngineer === '/'
        if (noFront && noBack && resultStr !== '通过') {
          if (!personMap['未设计']) {
            personMap['未设计'] = { completed: 0, uncompleted: 0 }
          }
          personMap['未设计'].uncompleted = XEUtils.add(personMap['未设计'].uncompleted, 1)
        }

        roleConfig.forEach(cfg => {
          const person = row[cfg.roleField]
          if (!person || person === '/') return

          // 处理多人情况（用"、"分隔）
          const persons = person.split('、')
          persons.forEach(p => {
            const key = `${p}(${cfg.label})`
            if (!personMap[key]) {
              personMap[key] = { completed: 0, uncompleted: 0 }
            }

            const dateVal = String(row[cfg.dateField] || '')
            const isCompleted = dateVal && !dateVal.includes('1900')
            if (isCompleted) {
              personMap[key].completed = XEUtils.add(personMap[key].completed, 1)
            } else {
              // 如果状态已经是"通过"，但该角色没有完成日期，则不计算为未完成
              if (resultStr === '通过') {
                // 不累加未完成
              } else {
                // 对于测试人员，如果没到测试阶段，未完成数量不计算
                const isTester = cfg.roleField === 'techTester' || cfg.roleField === 'businessTester'
                if (isTester && !isTestingPhase) {
                  // 不累加未完成
                } else {
                  personMap[key].uncompleted = XEUtils.add(personMap[key].uncompleted, 1)
                }
              }
            }
          })
        })
      })

      // 按岗位顺序（前端→后端→技术测试→业务测试）排列，同岗位内按总量降序，取 top 15
      const roleOrder = ['前端', '后端', '技术测试', '业务测试']
      const getRoleIndex = key => {
        for (let i = 0; i < roleOrder.length; i++) {
          if (key.endsWith(`(${roleOrder[i]})`)) return i
        }
        return roleOrder.length // 其他（未设计）排最后
      }

      const entries = Object.entries(personMap)
        .sort((a, b) => {
          const roleA = getRoleIndex(a[0])
          const roleB = getRoleIndex(b[0])
          if (roleA !== roleB) return roleA - roleB
          // 同岗位内按总量降序
          return XEUtils.subtract(
            XEUtils.add(b[1].completed, b[1].uncompleted),
            XEUtils.add(a[1].completed, a[1].uncompleted)
          )
        })
        .slice(0, 15)
        .reverse()

      return {
        names: entries.map(e => e[0]),
        completed: entries.map(e => e[1].completed || '-'),
        uncompleted: entries.map(e => e[1].uncompleted || '-')
      }
    },

    /**
     * 点击完成进度柱状图某人的柱子，过滤出对应数据并打开只读预览弹窗
     * @param {object} params - ECharts click 事件参数
     * @param {object} stats  - computeProgressStats 返回的统计结果
     */
    handleProgressBarClick(params) {
      if (!this.parentObj) return

      const clickedName = params.name // e.g. '张三(前端)'

      // 角色字段映射
      const roleConfigMap = {
        前端: { roleField: 'frontEngineer' },
        后端: { roleField: 'backEngineer' },
        技术测试: { roleField: 'techTester' },
        业务测试: { roleField: 'businessTester' }
      }

      // 解析姓名与角色
      const match = clickedName.match(/^(.+)\((.+)\)$/)
      if (!match) return
      const personName = match[1]
      const roleName = match[2]
      const cfg = roleConfigMap[roleName]
      if (!cfg) return

      // 过滤：该人在该角色列表中的所有问题（不区分已完成/未完成）
      const filtered = this.tableData.filter(row => {
        const persons = String(row[cfg.roleField] || '').split('、')
        return persons.includes(personName)
      })

      // 打开只读预览弹窗
      const modal = this.parentObj.$refs && this.parentObj.$refs.ReadonlySummaryModalRef
      if (modal) {
        modal.openModal(filtered, this.parentObj.gridColumns)
      }
    },

    // ======================== 图表2：工作量统计 ========================
    renderWorkloadChart() {
      const stats = this.computeWorkloadStats()
      const option = {
        tooltip: {
          trigger: 'axis',
          axisPointer: { type: 'shadow' },
          formatter: params => {
            let tip = `<b>${params[0].name}</b><br/>`
            params.forEach(p => {
              if (p.value !== null && p.value !== undefined && p.value !== 0) {
                tip += `${p.marker} ${p.seriesName}：${p.value}<br/>`
              }
            })
            return tip
          }
        },
        legend: {
          data: ['前端工作量', '后端工作量'],
          top: 5,
          textStyle: { fontSize: 11, color: '#666' }
        },
        grid: {
          left: 10,
          right: 15,
          top: 40,
          bottom: 5,
          containLabel: true
        },
        xAxis: {
          type: 'category',
          data: stats.names,
          axisLabel: {
            fontSize: 10,
            color: '#333',
            rotate: stats.names.length > 6 ? 30 : 0,
            interval: 0
          },
          axisLine: { lineStyle: { color: '#ddd' } },
          axisTick: { show: false }
        },
        yAxis: {
          type: 'value',
          axisLabel: { fontSize: 10, color: '#999' },
          splitLine: { lineStyle: { type: 'dashed', color: '#eee' } },
          boundaryGap: [0, '20%']
        },
        series: [
          {
            name: '前端工作量',
            type: 'bar',
            data: stats.frontWork,
            label: {
              show: true,
              position: 'top',
              formatter: params => (params.value > 0 ? params.value : ''),
              color: '#333',
              fontSize: 10
            },
            itemStyle: {
              color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
                { offset: 0, color: '#409eff' },
                { offset: 1, color: '#79bbff' }
              ]),
              borderRadius: [3, 3, 0, 0]
            },
            barMaxWidth: 30,
            stack: 'workload'
          },
          {
            name: '后端工作量',
            type: 'bar',
            data: stats.backWork,
            label: {
              show: true,
              position: 'top',
              formatter: params => (params.value > 0 ? params.value : ''),
              color: '#333',
              fontSize: 10
            },
            itemStyle: {
              color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
                { offset: 0, color: '#e6a23c' },
                { offset: 1, color: '#f0c78a' }
              ]),
              borderRadius: [3, 3, 0, 0]
            },
            barMaxWidth: 30,
            stack: 'workload'
          }
        ]
      }
      this.charts.workload.setOption(option, true)
    },

    // 计算工作量统计数据（每人一柱，按岗位归属）
    computeWorkloadStats() {
      // 前端人员 → 前端工作量；后端人员 → 后端工作量
      const personData = {} // { name: { front: number, back: number } }

      this.tableData.forEach(row => {
        // 前端工作量归属前端研发人员
        const frontPerson = row.frontEngineer
        const frontWork = parseFloat(row.frontWorkTime) || 0
        if (frontPerson && frontPerson !== '/' && frontWork > 0) {
          frontPerson.split('、').forEach(p => {
            if (!personData[p]) personData[p] = { front: 0, back: 0 }
            personData[p].front = XEUtils.add(personData[p].front, frontWork)
          })
        }

        // 后端工作量归属后端研发人员
        const backPerson = row.backEngineer
        const backWork = parseFloat(row.backWorkTime) || 0
        if (backPerson && backPerson !== '/' && backWork > 0) {
          backPerson.split('、').forEach(p => {
            if (!personData[p]) personData[p] = { front: 0, back: 0 }
            personData[p].back = XEUtils.add(personData[p].back, backWork)
          })
        }
      })

      // 按总工作量排序，取 Top 15
      const sorted = Object.entries(personData)
        .map(([name, data]) => ({ name, total: XEUtils.add(data.front, data.back), ...data }))
        .sort((a, b) => XEUtils.subtract(b.total, a.total))
        .slice(0, 15)

      return {
        names: sorted.map(s => s.name),
        // 每人只会有一种工作量（前端或后端），另一种为 0
        frontWork: sorted.map(s => s.front || null),
        backWork: sorted.map(s => s.back || null)
      }
    },

    // ======================== 图表3：类型分布 ========================
    renderTypeChart() {
      const stats = this.computeTypeStats()
      const colorMap = {
        错误: '#f56c6c',
        完善: '#409eff',
        新增: '#67c23a',
        性能: '#e6a23c'
      }
      const option = {
        tooltip: {
          trigger: 'item',
          formatter: '{b}：{c} ({d}%)'
        },
        legend: {
          orient: 'vertical',
          right: 10,
          top: 'center',
          textStyle: { fontSize: 11, color: '#666' },
          itemWidth: 12,
          itemHeight: 12
        },
        series: [
          {
            type: 'pie',
            radius: ['40%', '65%'],
            center: ['40%', '50%'],
            avoidLabelOverlap: true,
            itemStyle: {
              borderRadius: 4,
              borderColor: '#fff',
              borderWidth: 2
            },
            label: {
              show: true,
              fontSize: 11,
              formatter: '{b}\n{c}'
            },
            emphasis: {
              label: {
                show: true,
                fontSize: 13,
                fontWeight: 'bold'
              },
              itemStyle: {
                shadowBlur: 10,
                shadowOffsetX: 0,
                shadowColor: 'rgba(0, 0, 0, 0.2)'
              }
            },
            data: stats.map(item => ({
              value: item.count,
              name: item.name,
              itemStyle: { color: colorMap[item.name] || '#909399' }
            }))
          }
        ]
      }
      this.charts.type.setOption(option, true)
    },

    // 计算类型分布统计
    computeTypeStats() {
      const countMap = {}
      this.tableData.forEach(row => {
        const type = row.type || '未分类'
        countMap[type] = XEUtils.add(countMap[type] || 0, 1)
      })
      return Object.entries(countMap)
        .map(([name, count]) => ({ name, count }))
        .sort((a, b) => XEUtils.subtract(b.count, a.count))
    },

    // ======================== 图表4：收尾程度 ========================
    renderClosureChart() {
      const stats = this.computeClosureStats()
      const option = {
        tooltip: {
          trigger: 'item',
          formatter: params => {
            if (params.seriesType === 'gauge') {
              return `收尾进度：${params.value}%`
            }
            return `${params.name}：${params.value} (${params.percent}%)`
          }
        },
        series: [
          // 仪表盘显示收尾百分比
          {
            type: 'gauge',
            center: ['50%', '55%'],
            radius: '70%',
            startAngle: 200,
            endAngle: -20,
            min: 0,
            max: 100,
            splitNumber: 10,
            itemStyle: {
              color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
                { offset: 0, color: '#67c23a' },
                { offset: 0.5, color: '#e6a23c' },
                { offset: 1, color: '#f56c6c' }
              ])
            },
            progress: {
              show: true,
              width: 15,
              roundCap: true
            },
            pointer: {
              show: false
            },
            axisLine: {
              lineStyle: {
                width: 15,
                color: [[1, '#eee']]
              },
              roundCap: true
            },
            axisTick: { show: false },
            splitLine: { show: false },
            axisLabel: { show: false },
            anchor: { show: false },
            title: {
              show: true,
              offsetCenter: [0, '30%'],
              fontSize: 12,
              color: '#999'
            },
            detail: {
              valueAnimation: true,
              fontSize: 28,
              fontWeight: 'bold',
              offsetCenter: [0, '-5%'],
              formatter: '{value}%',
              color: '#333'
            },
            data: [
              {
                value: stats.closureRate,
                name: `${stats.passed} / ${stats.total}`
              }
            ]
          }
        ]
      }
      this.charts.closure.setOption(option, true)
    },

    // 计算收尾程度统计
    computeClosureStats() {
      let total = this.tableData.length
      let passed = 0
      const nodeMap = {}

      this.tableData.forEach(row => {
        const result = row.result || '未知'
        nodeMap[result] = XEUtils.add(nodeMap[result] || 0, 1)
        if (result === '通过') {
          passed = XEUtils.add(passed, 1)
        }
      })

      return {
        total,
        passed,
        closureRate: total > 0 ? Math.round(XEUtils.multiply(XEUtils.divide(passed, total), 100)) : 0,
        nodeDistribution: Object.entries(nodeMap)
          .map(([name, count]) => ({ name, count }))
          .sort((a, b) => XEUtils.subtract(b.count, a.count))
      }
    }
  }
}
</script>

<style lang="less" scoped>
.statistics-panel {
  margin: 0 10px 0 10px;
  // background: linear-gradient(135deg, #f8f9fc 0%, #eef1f8 100%);
  border-radius: 10px;
  // box-shadow: 0 2px 12px rgba(0, 0, 0, 0.06);
  overflow: hidden;
  animation: slideDown 0.3s ease-out;
}

@keyframes slideDown {
  from {
    opacity: 0;
    max-height: 0;
    transform: translateY(-10px);
  }
  to {
    opacity: 1;
    max-height: 500px;
    transform: translateY(0);
  }
}

.statistics-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 10px 16px 4px 16px;
}

.statistics-title {
  font-size: 14px;
  font-weight: 600;
  color: #303133;

  i {
    color: #409eff;
    margin-right: 6px;
  }
}

.statistics-summary {
  font-size: 12px;
  color: #909399;

  b {
    color: #409eff;
    font-size: 14px;
  }
}

.statistics-charts {
  display: flex;
  padding: 3px 6px;
  gap: 10px;
}

.chart-card {
  flex: 1;
  background: #fff;
  border-radius: 8px;
  box-shadow: 0 0 5px #d8d6d6;
  padding: 6px 8px;
  min-width: 0;
  transition: box-shadow 0.2s ease;

  &:hover {
    box-shadow: 0 3px 12px rgba(0, 0, 0, 0.08);
  }
}

.chart-card-title {
  font-size: 12px;
  font-weight: 500;
  color: #606266;
  padding-bottom: 4px;
  border-bottom: 1px solid #f0f0f0;
  margin-bottom: 4px;
}

.chart-container {
  width: 100%;
  height: 260px;
}
.chart-card-tip {
  font-size: 10px;
  color: #aaa;
  font-weight: normal;
  margin-left: 4px;
}

.chart-card.clickable {
  cursor: pointer;
}
</style>
